import { HTMLProps, useId } from 'react';
import { Theme, css } from '@emotion/react';
import * as RadixSwitch from '@radix-ui/react-switch';
import { Typography } from 'client/components/Typography';

export interface SwitchProps extends Omit<HTMLProps<HTMLDivElement>, 'label' | 'onChange' | 'children'> {
  label?: string | JSX.Element;
  checked: boolean;
  tabIndex?: number;
  component?: keyof JSX.IntrinsicElements;
  onChange?: (checked: boolean) => void;
}

export const Switch = ({ id: idProp, label, checked, disabled, tabIndex, component = 'button', onChange, ...other }: SwitchProps) => {
  const autoId = useId();
  const id = idProp || autoId;

  const Component = component as 'button';

  return (
    <div className="switch" css={containerStyle} {...other}>
      <Typography component="label" htmlFor={id} variant="body2" data-disabled={disabled} css={labelStyle}>
        {label || (checked ? 'Enabled' : 'Disabled')}
      </Typography>
      <RadixSwitch.Root
        id={id}
        checked={checked}
        disabled={disabled}
        tabIndex={tabIndex}
        css={switchStyle}
        onCheckedChange={onChange}
        asChild
      >
        <Component>
          <RadixSwitch.Thumb css={thumbStyle} />
        </Component>
      </RadixSwitch.Root>
    </div>
  );
};

const containerStyle = css`
  display: flex;
  align-items: center;
`;

// Larger click area for accessibility
const clickArea = css`
  &::after {
    content: '';
    position: absolute;
    top: -16px;
    left: -8px;
    width: calc(100% + 16px);
    height: calc(100% + 32px);
  }
`;

const labelStyle = css`
  position: relative;
  margin-right: 0.8rem;
  margin-bottom: -1px;
  user-select: none;
  cursor: pointer;

  ${clickArea}

  &[data-disabled='true'] {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const switchStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  align-items: center;
  font-size: 0;
  padding: 0;
  width: 32px;
  height: 20px;
  background: ${theme.color.grey[80]};
  border-radius: 16px;
  border: none;
  cursor: pointer;

  ${clickArea}

  &[data-state='checked'] {
    background: ${theme.color.primary[100]};
  }

  &:focus-visible {
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const onIconBackgroundUrl = (theme: Theme) =>
  `url('data:image/svg+xml,<svg width="12" height="12" viewBox="0 0 12 12" fill="none" ` +
  `xmlns="http://www.w3.org/2000/svg"><rect x="5" y="2" width="2" height="8" rx="1" ` +
  `fill="${theme.color.primary[100].replace('#', '%23')}"/></svg>')`;

const thumbStyle = (theme: Theme) => css`
  display: block;
  width: 12px;
  height: 12px;
  left: 4px;
  border-radius: 16px;
  background-color: #fff;
  position: relative;

  background-repeat: no-repeat;
  background-size: 10px;
  background-position: center;

  transition: all 200ms ${theme.easing.springy};

  &[data-state='checked'] {
    left: 16px;
    background-image: ${onIconBackgroundUrl(theme)};
  }
`;
